// api.js
import axios from 'axios';
import useAuthStore from '@store/useAuthStore';
import { message } from 'antd';
import { t } from '@/utils/translator';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true, // 쿠키 자동 전송
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('accessToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

let refreshPromise = null;

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const { config, response } = err;
    const ResErrMessage = response?.data?.detail;
    if (['Unauthorized', 'Invalid_Credentials'].includes(ResErrMessage)) {
      message.warning(t('비정상적 접근입니다. 로그아웃 되셨습니다.'));
    }

    if (['Expired_Session', 'Unauthorized', 'Invalid_Credentials'].includes(ResErrMessage)) {
      await useAuthStore.getState().logout();

      // 페이지 리다이렉트는 AuthInitializer나 ProtectedRoute에서 처리
      window.location.href = '/';
      return Promise.resolve(); // 페이지 이동 후 에러 전파 방지
    }

    if (response?.status === 401 && !config._retry) {
      config._retry = true;

      if (!refreshPromise) {
        refreshPromise = api
          .post('/auth/refresh')
          .then(({ data }) => {
            // 토큰 저장
            if (data.access_token) {
              localStorage.setItem('accessToken', data.access_token);
            }

            // 사용자 정보 업데이트
            if (data.user) {
              useAuthStore.getState().updateUser(data.user);
            }

            return data.access_token;
          })
          .catch(async (error) => {
            const { response } = error;
            const ResErrMessage = response?.data?.detail;

            if (['Unauthorized', 'Invalid_Credentials'].includes(ResErrMessage)) {
              message.warning(t('비정상적 접근입니다. 로그아웃 되셨습니다.'));
            }

            if (
              ['Expired_Session', 'Unauthorized', 'Invalid_Credentials'].includes(ResErrMessage)
            ) {
              await useAuthStore.getState().logout();

              // 페이지 리다이렉트
              window.location.href = '/';
              return Promise.resolve(); // 에러 전파 방지
            }

            // 다른 refresh 에러는 원래 에러 전파
            throw error;
          })
          .finally(() => {
            refreshPromise = null; // 다시 초기화
          });
      }

      return refreshPromise.then((newToken) => {
        config.headers.Authorization = `Bearer ${newToken}`;
        return api(config); // 원래 요청 재시도
      });
    }

    return Promise.reject(err);
  }
);

export default api;